import React, { useState, useEffect } from 'react';
import { loadStripe } from '@stripe/stripe-js';
import { Elements, PaymentElement, useStripe, useElements } from '@stripe/react-stripe-js';
import { X, Loader2, AlertTriangle, CheckCircle } from 'lucide-react';
import { functions } from '../../firebase/config';
import { httpsCallable } from 'firebase/functions';
import { formatCurrency } from '../../utils/formatters';

const stripeKey = process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY;

// payall-8d5c1f37 -- Seam 11 Part B: portal "Pay All". ONE payment for the customer's full
// outstanding balance, charged as a single direct charge on the connected account (the
// server computes the amount from live balances -- the client never sends it). On success
// the charge becomes ONE account credit spread across the invoices (recordAccountPaymentAndSpread
// inline; the stripeWebhook is the backstop). Deliberately a SEPARATE component from the
// per-invoice PaymentModal -- this rail is account-level, not invoice-shaped.

// payhide-7e4c1b05 — shown when the payment can't start for any unrecognised reason. Never the
// server's own text.
const START_FAILED_MESSAGE = "We couldn't start this payment. Please try again, or contact the business to arrange payment.";

const PayAllCheckoutForm = ({ amount, connectedAccountId, onSuccess, onError }) => {
    const stripe = useStripe();
    const elements = useElements();
    const [isProcessing, setIsProcessing] = useState(false);

    const handleSubmit = async (event) => {
        event.preventDefault();
        if (!stripe || !elements) return;
        setIsProcessing(true);

        // Redirect methods leave the app and land on /portal/payment-result (which reads the
        // PI status and records nothing -- the webhook records). acct scopes the retrieve to
        // the connected account this direct-charge PI lives on.
        const { error, paymentIntent } = await stripe.confirmPayment({
            elements,
            confirmParams: {
                return_url: `${window.location.origin}/portal/payment-result?acct=${connectedAccountId}`,
            },
            redirect: 'if_required',
        });

        if (error) {
            onError(error.message);
            setIsProcessing(false);
            return;
        }

        // Inline completion (card): record + spread now. Idempotent with the webhook, so a
        // failure here is non-fatal -- the webhook (and the reconcile net) still record it.
        try {
            const recordFn = httpsCallable(functions, 'recordAccountPaymentAndSpread');
            await recordFn({ stripePaymentIntentId: paymentIntent?.id });
        } catch (recordErr) {
            console.error('Pay All recorded on Stripe but inline record failed (webhook will record):', recordErr);
        }
        onSuccess();
    };

    return (
        <form onSubmit={handleSubmit} className="space-y-4">
            <PaymentElement />
            <button
                disabled={!stripe || isProcessing}
                className="w-full bg-green-600 text-white py-3 rounded-lg font-bold hover:bg-green-700 disabled:opacity-50 flex justify-center items-center gap-2"
            >
                {isProcessing ? <Loader2 className="animate-spin" /> : `Pay ${formatCurrency(amount)}`}
            </button>
        </form>
    );
};

const PortalPayAllModal = ({ isOpen, onClose, onSuccess }) => {
    // clientSecret + stripePromise are set together so the Stripe instance is always scoped
    // to the exact connected account that created the PaymentIntent.
    const [paymentData, setPaymentData] = useState(null); // { clientSecret, stripePromise, connectedAccountId, amount }
    const [error, setError] = useState(null);
    const [success, setSuccess] = useState(false);

    useEffect(() => {
        if (isOpen) { setPaymentData(null); setError(null); setSuccess(false); }
    }, [isOpen]);

    // Mint the account-level PaymentIntent once the modal opens. Cancellation guard prevents
    // React StrictMode's double-invoke from creating two PaymentIntents.
    useEffect(() => {
        if (!isOpen || paymentData || !stripeKey) return;
        let cancelled = false;
        const createIntent = httpsCallable(functions, 'createAccountPaymentIntent');
        createIntent()
            .then((result) => {
                if (cancelled) return;
                const { clientSecret, connectedAccountId, amount } = result.data || {};
                if (!clientSecret) { setError(START_FAILED_MESSAGE); return; }
                const stripePromise = connectedAccountId
                    ? loadStripe(stripeKey, { stripeAccount: connectedAccountId })
                    : loadStripe(stripeKey);
                setPaymentData({ clientSecret, stripePromise, connectedAccountId, amount });
            })
            .catch((err) => {
                if (cancelled) return;
                console.error('createAccountPaymentIntent error:', err);
                const notOnboarded = err?.code === 'functions/failed-precondition' &&
                    /not set up to receive payments/i.test(err?.message || '');
                const nothingDue = /no outstanding balance/i.test(err?.message || '');
                if (notOnboarded) {
                    setError("This business isn't set up to accept online payments yet. Please contact them to arrange payment.");
                } else if (nothingDue) {
                    setError('You have no outstanding balance to pay.');
                } else {
                    setError(START_FAILED_MESSAGE);
                }
            });
        return () => { cancelled = true; };
    }, [isOpen, paymentData]);

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-xl shadow-2xl w-full max-w-md overflow-hidden max-h-[90vh] overflow-y-auto">
                <div className="flex justify-between items-center p-4 border-b sticky top-0 bg-white z-10">
                    <h2 className="text-xl font-bold text-gray-800">Pay All Invoices</h2>
                    <button onClick={onClose} className="text-gray-500 hover:text-gray-700"><X size={24} /></button>
                </div>

                <div className="p-6">
                    {!stripeKey ? (
                        <div className="text-center p-4 bg-yellow-50 rounded-lg text-yellow-800">
                            <AlertTriangle className="h-8 w-8 mx-auto mb-2 text-yellow-600" />
                            <p className="font-bold">Payments Not Configured</p>
                        </div>
                    ) : (
                        <>
                            {error && (
                                <div className="mb-4 bg-red-50 text-red-600 p-3 rounded-lg text-sm flex items-start gap-2">
                                    <AlertTriangle size={16} className="mt-0.5 shrink-0" />
                                    {error}
                                </div>
                            )}

                            {paymentData ? (
                                <>
                                    <p className="text-sm text-gray-600 mb-4">
                                        You're paying your full outstanding balance of{' '}
                                        <span className="font-bold text-gray-900">{formatCurrency(paymentData.amount)}</span>{' '}
                                        in one payment.
                                    </p>
                                    <Elements
                                        key={paymentData.connectedAccountId || 'platform'}
                                        stripe={paymentData.stripePromise}
                                        options={{ clientSecret: paymentData.clientSecret, appearance: { theme: 'stripe' } }}
                                    >
                                        <PayAllCheckoutForm
                                            amount={paymentData.amount}
                                            connectedAccountId={paymentData.connectedAccountId}
                                            onSuccess={() => { setSuccess(true); setTimeout(() => { onClose(); if (onSuccess) onSuccess(); }, 2000); }}
                                            onError={setError}
                                        />
                                    </Elements>
                                </>
                            ) : (
                                !error && (
                                    <div className="flex justify-center p-8">
                                        <Loader2 className="animate-spin h-8 w-8 text-green-600" />
                                    </div>
                                )
                            )}

                            {success && (
                                <div className="mt-4 flex items-center gap-2 p-3 bg-green-50 border border-green-200 rounded-lg text-green-700 text-sm">
                                    <CheckCircle size={16} className="shrink-0" />
                                    Payment successful! Updating your invoices...
                                </div>
                            )}
                        </>
                    )}
                </div>
            </div>
        </div>
    );
};

export default PortalPayAllModal;
